import { CSSProperties, ReactNode, useCallback } from 'react';
import { css } from '@emotion/react';
import { styles } from '@frontend/design-system';
import { TOTAL_HOURS } from '../constants';
import { useZoomStore } from '../store/zoom.store';
import { getHoverMinutesFromEvent } from '../utils';
import { CalendarTooltip, useCalendarTooltip } from './calendar-tooltip';

interface CalendarGridInteractionSurfaceProps {
  containerRef: React.RefObject<HTMLDivElement>;
  wrapperRef?: React.RefObject<HTMLElement>;
  style: CSSProperties;
  isGridBody: boolean;
  isAllowDragCreateSelection: boolean;
  isDragCreateSelectionStarted: boolean;
  startDragCreateSelection: (columnId?: string, groupId?: string, totalMinutes?: number) => void;
  progressDragCreateSelection: (totalMinutes?: number) => void;
  endDragCreateSelection: () => void;
  children: ReactNode;
}

// Owns the mouse-driven tooltip state so its re-renders stay isolated from the (expensive) grid content in `children`.
export const CalendarGridInteractionSurface = ({
  containerRef,
  wrapperRef,
  style,
  isGridBody,
  isAllowDragCreateSelection,
  isDragCreateSelectionStarted,
  startDragCreateSelection,
  progressDragCreateSelection,
  endDragCreateSelection,
  children,
}: CalendarGridInteractionSurfaceProps) => {
  const { resolvedRowHeightPx } = useZoomStore(['resolvedRowHeightPx']);
  const tooltipInfo = useCalendarTooltip(containerRef);
  const hideTooltip = tooltipInfo.hideTooltip;
  const handleTooltipMouseMove = tooltipInfo.onMouseMove;

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (!containerRef.current) return;
      const targetElement = e.target instanceof HTMLElement ? e.target : null;
      const columnElement = targetElement?.closest('[data-column-id]') as HTMLElement | null;
      const hoveredEventElement = targetElement?.closest('[data-event-id]') as HTMLElement | null;
      const shouldHideTooltip = !isGridBody || isDragCreateSelectionStarted || !columnElement;

      if (shouldHideTooltip) {
        hideTooltip();
      } else {
        handleTooltipMouseMove(e.clientX, e.clientY, hoveredEventElement?.dataset.eventId);
      }

      if (isDragCreateSelectionStarted) {
        const totalMinutes = getHoverMinutesFromEvent(e.clientY, containerRef.current, resolvedRowHeightPx);
        progressDragCreateSelection(totalMinutes);
        return;
      }
    },
    [
      containerRef,
      isGridBody,
      isDragCreateSelectionStarted,
      hideTooltip,
      handleTooltipMouseMove,
      resolvedRowHeightPx,
      progressDragCreateSelection,
    ]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!containerRef.current || !isAllowDragCreateSelection) return;

      const targetElement =
        e.target instanceof HTMLElement ? (e.target.closest('[data-column-id]') as HTMLElement | null) : null;
      const { columnId, groupId } = targetElement?.dataset ?? {};

      const totalMinutes = getHoverMinutesFromEvent(e.clientY, containerRef.current, resolvedRowHeightPx);
      startDragCreateSelection(columnId, groupId, totalMinutes);
      hideTooltip();
    },
    [containerRef, hideTooltip, isAllowDragCreateSelection, resolvedRowHeightPx, startDragCreateSelection]
  );

  const referenceProps = tooltipInfo.getReferenceProps({
    onMouseMove: handleMouseMove,
    onMouseUp: endDragCreateSelection,
    onMouseLeave: hideTooltip,
    onMouseDown: handleMouseDown,
  });

  const mergedRef = (node: HTMLElement | null) => {
    (containerRef as React.MutableRefObject<HTMLElement | null>).current = node;
    if (wrapperRef) {
      (wrapperRef as React.MutableRefObject<HTMLElement | null>).current = node;
    }
  };

  return (
    <section ref={mergedRef} {...referenceProps} css={[styles.smallScrollbar, containerStyle]} style={style}>
      {children}
      <CalendarTooltip
        hoverMinutes={tooltipInfo.hoverMinutes}
        hoveredEventId={tooltipInfo.hoveredEventId}
        containerRef={tooltipInfo.refs.setFloating}
        floatingStyles={tooltipInfo.floatingStyles}
        getFloatingProps={tooltipInfo.getFloatingProps}
      />
    </section>
  );
};

const containerStyle = css({
  '--time-column-width': '60px',
  '--header-height': 'max-content',
  '--group-separator-width': 'var(--time-column-width)',
  '--total-mins': TOTAL_HOURS * 60,
  '--total-hours': TOTAL_HOURS,
  height: '100%',
  display: 'grid',
  position: 'relative',
  /* Critical for performance: layout isolation */
  contain: 'layout size',
  overflow: 'auto',

  // snap scroll styles
  scrollSnapType: 'x mandatory',
  scrollBehavior: 'smooth',
  scrollPaddingLeft: 'var(--time-column-width)',
});
